import { createFeature, createReducer, on } from '@ngrx/store';
import { UserActions } from './user.actions';
import { UserFormMode, UserState } from './user-state.model';
import { FIRST_PAGE } from './user-state.constants';

const initialState: UserState = {
  users: [],
  count: 0,
  page: FIRST_PAGE,
  search: '',
  isLoadingUsers: false,
  formToken: 0,
  mode: 'idle',
  selectedUser: null,
  companies: [],
  companyDetail: null,
  multiBuyerEntities: [],
  projectManagers: [],
  isSaving: false,
  apiToken: '',
  error: null,
};

/**
 * Per-user fields only: company data is keyed by company, not by user.
 * `formToken` always moves, so reopening the same mode still rebuilds the form:
 * two `startCreate` in a row leave `mode` and `selectedUser` untouched.
 */
const resetForm = (
  state: UserState,
  mode: UserFormMode,
): Pick<
  UserState,
  'mode' | 'selectedUser' | 'apiToken' | 'error' | 'formToken'
> => ({
  mode,
  selectedUser: null,
  apiToken: '',
  error: null,
  formToken: state.formToken + 1,
});

export const userFeatureKey = 'user';

export const userReducer = createReducer(
  initialState,

  on(UserActions.loadUsers, (state, { page }) => ({
    ...state,
    page,
    isLoadingUsers: true,
    error: null,
  })),
  /**
   * `selectedUser` is the copy taken when the row was picked, so a reload after
   * a write would leave the form reading the old status. Re-read it from the
   * new page; keep the copy when the reload no longer carries that user.
   */
  on(UserActions.loadUsersSuccess, (state, { users, count }) => ({
    ...state,
    users,
    count,
    selectedUser: state.selectedUser
      ? (users.find((user) => user.id === state.selectedUser?.id) ??
        state.selectedUser)
      : null,
    isLoadingUsers: false,
    error: null,
  })),
  on(UserActions.loadUsersFailure, (state, { error }) => ({
    ...state,
    users: [],
    count: 0,
    isLoadingUsers: false,
    error,
  })),

  on(UserActions.changeSearch, (state, { search }) => ({
    ...state,
    search,
    page: FIRST_PAGE,
    isLoadingUsers: true,
    error: null,
  })),

  on(UserActions.selectUser, (state, { userId }) => ({
    ...state,
    ...resetForm(state, 'edit'),
    selectedUser: state.users.find((user) => user.id === userId) ?? null,
  })),
  on(UserActions.startCreate, (state) => ({
    ...state,
    ...resetForm(state, 'create'),
  })),
  on(UserActions.clearForm, (state) => ({
    ...state,
    ...resetForm(state, 'idle'),
  })),
  on(UserActions.resetState, () => initialState),

  on(UserActions.loadCompaniesSuccess, (state, { companies }) => ({
    ...state,
    companies,
    error: null,
  })),
  on(UserActions.loadCompaniesFailure, (state, { error }) => ({
    ...state,
    companies: [],
    error,
  })),

  on(UserActions.loadCompanyDetail, (state) => ({
    ...state,
    companyDetail: null,
    multiBuyerEntities: [],
    projectManagers: [],
    error: null,
  })),
  on(UserActions.loadCompanyDetailSuccess, (state, { companyDetail }) => ({
    ...state,
    companyDetail,
    error: null,
  })),
  on(UserActions.loadCompanyDetailFailure, (state, { error }) => ({
    ...state,
    companyDetail: null,
    error,
  })),

  on(UserActions.loadMultiBuyerEntitiesSuccess, (state, { entities }) => ({
    ...state,
    multiBuyerEntities: entities,
    error: null,
  })),
  on(UserActions.loadMultiBuyerEntitiesFailure, (state, { error }) => ({
    ...state,
    multiBuyerEntities: [],
    error,
  })),

  on(UserActions.loadProjectManagersSuccess, (state, { projectManagers }) => ({
    ...state,
    projectManagers,
    error: null,
  })),
  on(UserActions.loadProjectManagersFailure, (state, { error }) => ({
    ...state,
    projectManagers: [],
    error,
  })),

  on(UserActions.saveUser, UserActions.toggleUserStatus, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(
    UserActions.saveUserSuccess,
    UserActions.toggleUserStatusSuccess,
    (state) => ({
      ...state,
      isSaving: false,
      error: null,
    }),
  ),
  on(
    UserActions.saveUserFailure,
    UserActions.toggleUserStatusFailure,
    (state, { error }) => ({
      ...state,
      isSaving: false,
      error,
    }),
  ),

  on(UserActions.loadApiTokenSuccess, (state, { token }) => ({
    ...state,
    apiToken: token,
    error: null,
  })),
  on(UserActions.loadApiTokenFailure, (state, { error }) => ({
    ...state,
    apiToken: '',
    error,
  })),
  on(UserActions.clearApiToken, (state) => ({
    ...state,
    apiToken: '',
  })),
);

export const userFeature = createFeature({
  name: userFeatureKey,
  reducer: userReducer,
});
